import { SkillSet } from "./SkillSet";
import { DemandStatus } from "../enums/DemandStatus";
import { ManuallyPlannedTime } from "./ManuallyPlannedTime";
import { AssignmentComment } from "./AssignmentComment";
import { Employee } from "./Employee";
import { Project } from "./Project";
import { Timestamp } from "firebase/firestore";
import { Language } from "./Language";


export class AssignmentDemand {

    static AllDemandStatuses = [
        DemandStatus.Open,
        DemandStatus.OpenOnHold,
        DemandStatus.OpenSourcing,
        DemandStatus.Resolved,
        DemandStatus.Dropped,
        DemandStatus.Lost,
        DemandStatus.ClarificationNeeded
    ];
    id: string;
    role: string;
    startDate: Date | null;
    endDate: Date | null;
    skills: SkillSet;
    languages: Language[];
    location: string;
    originatingProjectId: string
    remote: boolean;
    status: string;
    priority: string;
    customer: string;
    description: string;
    seniority: string;
    pdTotalPlanned: number;
    pdTotalActual: number;
    pdTotalRemaining: number;
    pdTotalForecast: number;
    pdTotalPlannedHours: number;
    pdTotalActualHours: number;
    pdTotalRemainingHours: number;
    pdTotalForecastHours: number;
    pdTotalPlannedDays: number;
    pdTotalActualDays: number;
    pdTotalRemainingDays: number
    pdTotalForecastDays: number
    projectId: string;
    employeeId: string;
    suggestions: string[];
    responsible: string;
    delegates: string[];
    createdDate: Date;
    dueDate: Date;
    chanceToWin: number;
    manuallyPlanned: boolean;
    comments: AssignmentComment[];
    assignments: AssignmentDemand[] | null;
    isNew: boolean = false;
    plannedWeeks: { [key: string]: number; } = {};
    employee: Employee | null;
    project: Project | null;
    projectName: string;
    cw_planned: number;
    responsibleNotificationIssued?: boolean;
    assignedNotificationIssued?: boolean;
    demandNumber: string;

    constructor(data: Partial<AssignmentDemand> = {}, manualPlanning: ManuallyPlannedTime[] = []) {

        if (!data.id || data.id === '')
            data.id = self.crypto.randomUUID();

        this.id = data.id || '';
        this.employee = data.employee || null;
        this.assignments = data.assignments || null;
        this.project = data.project || null;
        this.role = data.role || '';
        this.startDate = data.startDate instanceof Date ? data.startDate : (data.startDate ? new Date((data.startDate as Timestamp).seconds * 1000) : null);
        this.endDate = data.endDate instanceof Date ? data.endDate : (data.endDate ? new Date((data.endDate as Timestamp).seconds * 1000) : null);
        this.skills = data.skills || new SkillSet();

        if (data.skills && data.skills.skills)
            data.skills = new SkillSet(data.skills.skills);
        else if (data.skills)
            data.skills = new SkillSet((data as any).skills);

        this.skills = data.skills || new SkillSet();
        this.location = data.location || '';
        this.remote = data.remote || false;
        this.status = data.status || '';
        this.priority = data.priority || '';
        this.originatingProjectId = data.originatingProjectId || '';
        this.customer = data.customer || '';
        this.description = data.description || '';
        this.seniority = data.seniority || '';
        this.pdTotalPlanned = Math.round((data.pdTotalPlanned || 0) * 100) / 100;
        this.projectId = data.projectId || '';
        this.employeeId = data.employeeId || '';
        this.suggestions = data.suggestions || [];
        this.responsible = data.responsible || '';
        this.delegates = data.delegates || [];
        this.createdDate = data.createdDate instanceof Date ? data.createdDate : (data.createdDate ? new Date((data.createdDate as Timestamp).seconds * 1000) : new Date());
        this.dueDate = data.dueDate instanceof Date ? data.dueDate : (data.dueDate ? new Date((data.dueDate as Timestamp).seconds * 1000) : new Date());
        this.chanceToWin = data.chanceToWin || 0;
        this.manuallyPlanned = data.manuallyPlanned || false;
        this.comments = data.comments || [];
        this.pdTotalActual = data.pdTotalActual || 0;
        this.pdTotalRemaining = data.pdTotalRemaining || 0;
        this.pdTotalForecast = data.pdTotalForecast || 0;
        this.pdTotalPlannedHours = data.pdTotalPlannedHours || 0;
        this.pdTotalActualHours = data.pdTotalActualHours || 0;
        this.pdTotalForecastHours = data.pdTotalForecastHours || 0;
        this.pdTotalPlannedDays = data.pdTotalPlannedDays || 0;
        this.pdTotalActualDays = data.pdTotalActualDays || 0;
        this.pdTotalRemainingDays = data.pdTotalRemainingDays || 0;
        this.pdTotalForecastDays = data.pdTotalForecastDays || 0;
        this.pdTotalRemainingHours = data.pdTotalRemainingHours || 0;
        this.projectName = data.projectName || '';
        this.cw_planned = data.cw_planned || 0;
        this.languages = data.languages || [];
        this.responsibleNotificationIssued = data.responsibleNotificationIssued || false;
        this.assignedNotificationIssued = data.assignedNotificationIssued || false;
        this.demandNumber = data.demandNumber || '';

        manualPlanning?.forEach((mp) => {
            this.plannedWeeks[`cw_${mp.week}_${mp.year}`] = mp.hours;
        });
    }


    static createNew(obj: any) {
        let assignment = new AssignmentDemand(obj);
        assignment.isNew = true;
        return assignment;
    }

    toFireStoreObject(): object {
        return {
            id: this.id,
            role: this.role,
            startDate: this.startDate ? this.startDate : null,
            endDate: this.endDate ? this.endDate : null,
            skills: this.skills && this.skills.skills ? this.skills.skills.map(x => {
                return {
                    category: x.category,
                    level: x.level,
                    skill: x.skill,
                    isRequired: x.isRequired ?? true,
                }
            }) : [],
            location: this.location,
            remote: this.remote,
            status: this.status,
            priority: this.priority,
            customer: this.customer,
            description: this.description,
            seniority: this.seniority,
            pdTotalPlanned: this.pdTotalPlanned,
            projectId: this.projectId,
            employeeId: this.employeeId,
            suggestions: this.suggestions,
            originatingProjectId: this.originatingProjectId,
            responsible: this.responsible,
            delegates: this.delegates,
            createdDate: this.createdDate ? this.createdDate : null,
            dueDate: this.dueDate ? this.dueDate : null,
            chanceToWin: this.chanceToWin,
            manuallyPlanned: this.manuallyPlanned,
            languages: this.languages.map(l => ({
                language: l.language,
                level: l.level,
                isNative: l.isNative || false,
                certified: l.certified || false,
                isRequired: l.isRequired || false
            })),
            responsibleNotificationIssued: this.responsibleNotificationIssued ? this.responsibleNotificationIssued : false,
            assignedNotificationIssued: this.assignedNotificationIssued ? this.assignedNotificationIssued : false,
            demandNumber: this.demandNumber,
            comments: this.comments ? this.comments.map(x => {
                return {
                    createdBy: x.createdBy,
                    createdDate: x.createdDate,
                    message: x.message,
                }
            }) : [],
            manualPlanning: this.plannedWeeks ? this.getManualPlanning().map(x => {
                return {
                    hours: x.hours,
                    week: x.week,
                    year: x.year
                }
            }) : [],
        };

    }


    getManualPlanning(): ManuallyPlannedTime[] {
        return Object.keys(this.plannedWeeks)
            .filter(key => key.startsWith('cw_')).filter(key => this.plannedWeeks[key] > 0)
            .map(key => new ManuallyPlannedTime(this.plannedWeeks[key], Number(key.split('_')[1]), Number(key.split('_')[2])));
    }
}
